"use client";

// Outage state context — fetches the active vault_broadcasts row in
// channel='outage' (single-active invariant enforced by the operator
// CLI; if multiple are active, takes the most recent). Exposed via
// useOutage() for BannerSlot's condition function and OutageBanner's
// render. Polls every 60s; fast surfacing isn't critical because the
// operator (Collin) sees the row land via `outage-banner.mjs status`
// immediately. 60s ceiling is acceptable user-side lag.
//
// Spec: md/operations/outage-templates.md (locked-copy + playbook)
//       md/operations/launch-runbook.md §Communication (operator surface)
//       md/NextUp.md Cluster L #121-followup
//
// Why context not module-singleton: the provider's useEffect lifecycle
// is the right place to manage polling cleanup (clearInterval on
// unmount). Module singletons leak intervals across HMR.

import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase";

const OutageContext = createContext({ activeOutage: null });

const POLL_INTERVAL_MS = 60000;

export function OutageProvider({ children }) {
  const [activeOutage, setActiveOutage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOutage = async () => {
      try {
        const { data, error } = await supabase
          .from("vault_broadcasts")
          .select("id, title, content, created_at")
          .eq("channel", "outage")
          .eq("active", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          // Silent fail — schema-not-migrated or RLS reject. Banner stays hidden.
          setActiveOutage(null);
          return;
        }
        setActiveOutage(data || null);
      } catch {
        if (!cancelled) setActiveOutage(null);
      }
    };

    fetchOutage();
    const id = setInterval(fetchOutage, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <OutageContext.Provider value={{ activeOutage }}>
      {children}
    </OutageContext.Provider>
  );
}

export function useOutage() {
  return useContext(OutageContext);
}
